import { useCallback, useEffect, useRef, useState } from 'react';
import { Card, EmptyState, SkeletonRows } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import {
  adminApi,
  type AiAgentEntry,
  type AiBusinessReport,
  type AiReportAccess,
  type AiReportEnvelope,
  type AiReportKind,
  type AiReportPeriod,
} from '@/shared/api/adminApi';
import { downloadCsv } from '@/shared/lib/downloadCsv';
import { toast } from '@/components/ui/Toast';
import {
  AgentsReportView,
  AgentsSidebar,
  BusinessReportView,
  CampaignsReportView,
  ReportStatusNote,
} from './insights/ReportViews';

/**
 * "תובנות מוקד — סיכומי AI": שלושה דוחות עומק שנוצרים כל לילה (כלל עסקי /
 * נציגים / קמפיינים), עם דפדוף יומי/חודשי/שנתי, הורדת PDF והפקה on-demand.
 * צפייה נשלטת ע"י הבעלים במסך "תפקידים".
 */

const KIND_TABS: { id: AiReportKind; label: string }[] = [
  { id: 'business', label: 'כלל עסקי' },
  { id: 'agents', label: 'נציגים' },
  { id: 'campaigns', label: 'קמפיינים' },
];

const PERIODS: { id: AiReportPeriod; label: string }[] = [
  { id: 'daily', label: 'יומי' },
  { id: 'monthly', label: 'חודשי' },
  { id: 'yearly', label: 'שנתי' },
];

function yesterdayIso(): string {
  const d = new Date(Date.now() - 24 * 3600 * 1000);
  return d.toISOString().slice(0, 10);
}

/** תאריך העוגן שנשלח לשרת עבור כל תקופה (השרת מנרמל לתחילת התקופה). */
export function anchorFor(period: AiReportPeriod, date: string): string {
  if (period === 'monthly') return `${date.slice(0, 7)}-01`;
  if (period === 'yearly') return `${date.slice(0, 4)}-01-01`;
  return date;
}

export function Insights() {
  const [access, setAccess] = useState<AiReportAccess | null | 'denied'>(null);
  const [tab, setTab] = useState<AiReportKind>('business');
  const [period, setPeriod] = useState<AiReportPeriod>('daily');
  const [date, setDate] = useState(yesterdayIso());
  const [available, setAvailable] = useState<{ date: string; status: string }[]>([]);
  const [data, setData] = useState<AiReportEnvelope | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [activeAgent, setActiveAgent] = useState<number | null>(null);
  const pollRef = useRef<number | null>(null);

  useEffect(() => {
    void adminApi.aiReportAccess()
      .then((a) => setAccess(a.can_view ? a : 'denied'))
      .catch((e) => {
        // 403 = אין הרשאה; כל שגיאה אחרת → לא נועלים במסך "טוען" לנצח,
        // נאפשר צפייה (השרת אוכף ממילא) ונציג שגיאה.
        if ((e as { response?: { status?: number } })?.response?.status === 403) { setAccess('denied'); return; }
        toast('שגיאה בטעינת הרשאות — נסה לרענן', 'danger');
        setAccess({ allowed_roles: [], assignable_roles: [], can_view: true, can_edit: false });
      });
  }, []);

  const loadAvailable = useCallback(() => {
    void adminApi.aiReportDates(period, Number(date.slice(0, 4)))
      .then(setAvailable)
      .catch(() => setAvailable([]));
  }, [period, date]);

  const load = useCallback((silent = false) => {
    if (!silent) { setLoading(true); setError(false); }
    void adminApi.aiReport(tab, period, anchorFor(period, date))
      .then((d) => { setData(d); setError(false); })
      .catch(() => { if (!silent) { setError(true); toast('שגיאה בטעינת הדוח', 'danger'); } })
      .finally(() => setLoading(false));
  }, [tab, period, date]);

  const allowed = access !== null && access !== 'denied';
  useEffect(() => { if (allowed) load(); }, [allowed, load]);
  useEffect(() => { if (allowed) loadAvailable(); }, [allowed, loadAvailable]);

  // דוח בהפקה → ריענון אוטומטי כל 8 שניות עד שיהיה מוכן. interval (ולא
  // setTimeout על deps) כי הסטטוס נשאר 'pending' בין רענונים — timeout היה
  // מת אחרי ריצה אחת (ה-effect לא רץ מחדש כשה-deps זהים).
  const pending = data?.status === 'pending' || data?.status === 'running';
  useEffect(() => {
    if (!pending) return;
    pollRef.current = window.setInterval(() => load(true), 8000);
    return () => { if (pollRef.current) window.clearInterval(pollRef.current); };
  }, [pending, load]);

  async function generate() {
    setGenerating(true);
    try {
      await adminApi.generateAiReport(period, anchorFor(period, date));
      toast('הדוח בהפקה — יוצג כאן בעוד רגעים', 'ok');
      load(true);
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast(msg ?? 'שגיאה בהפקת הדוח', 'danger');
    } finally {
      setGenerating(false);
    }
  }

  async function downloadPdf() {
    try {
      await downloadCsv('/admin/ai-reports/pdf', { kind: tab, period, date: anchorFor(period, date) },
        `ai-report-${tab}-${anchorFor(period, date)}.pdf`);
    } catch {
      toast('אין דוח מוכן להורדה לתקופה זו', 'danger');
    }
  }

  /** דפדוף בין תקופות שקיימים להן דוחות. */
  function step(dir: 1 | -1) {
    const dates = available.map((a) => a.date).sort();
    const anchor = anchorFor(period, date);
    const idx = dates.indexOf(anchor);
    const next = idx >= 0
      ? dates[idx + dir]
      : (dir === -1 ? [...dates].reverse().find((d) => d < anchor) : dates.find((d) => d > anchor));
    if (next) setDate(next);
  }

  if (access === 'denied') {
    return (
      <div style={{ padding: 'var(--gap)' }}>
        <EmptyState icon="ai" title="אין לך הרשאה לצפות בדוחות ה-AI" description='הבעלים יכול להעניק גישה במסך "תפקידים".' />
      </div>
    );
  }
  if (!access) return <div style={{ padding: 'var(--gap)' }}><Card><SkeletonRows rows={6} /></Card></div>;

  const agents: AiAgentEntry[] = (data?.report as { agents?: AiAgentEntry[] })?.agents ?? [];
  const showSidebar = tab !== 'business' && data?.status === 'done' && agents.length > 1;

  // 07/09/2026: אזור התוכן עוצב מחדש (insights.css). 28/09/2026 (GoCall v2):
  // שם המסך רק בסרגל העליון; סוג הדוח כבורר מקטעים מול תאריך/תקופה/PDF,
  // כרטיסים בסגנון v2. הלוגיקה והתוכן לא השתנו.
  return (
    <div className="ai-insights page-enter">
      <h1 className="sr-only">סיכומי AI</h1>
      <div className="ai-header">
        <div className="seg" role="tablist" aria-label="סוג דוח">
          {KIND_TABS.map((t) => (
            <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>
        <span className="ai-spacer" />
        <div className="ai-toolbar">
          <div className="ai-dategroup">
            <button type="button" className="ai-datenav" aria-label="תקופה קודמת" onClick={() => step(-1)}><Icon name="arrowR" size={16} /></button>
            {period === 'daily' && (
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} dir="ltr" aria-label="תאריך" />
            )}
            {period === 'monthly' && (
              <input type="month" value={date.slice(0, 7)} onChange={(e) => e.target.value && setDate(`${e.target.value}-01`)} dir="ltr" aria-label="חודש" />
            )}
            {period === 'yearly' && (
              <select value={date.slice(0, 4)} onChange={(e) => setDate(`${e.target.value}-01-01`)} dir="ltr" aria-label="שנה">
                {Array.from({ length: Math.max(1, new Date().getFullYear() - 2025) }, (_, i) => String(2026 + i)).map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            )}
            <button type="button" className="ai-datenav" aria-label="תקופה הבאה" onClick={() => step(1)}><Icon name="arrowL" size={16} /></button>
          </div>

          <div className="ai-seg" role="group" aria-label="תקופה">
            {PERIODS.map((p) => (
              <button key={p.id} type="button" aria-pressed={period === p.id} onClick={() => setPeriod(p.id)}>
                {p.label}
              </button>
            ))}
          </div>

          <button type="button" className="btn" onClick={downloadPdf} disabled={data?.status !== 'done'}>
            <Icon name="download" size={15} /> PDF
          </button>
        </div>
      </div>
      <p className="ai-subtitle">ניתוח ביצועים, שיחות ותובנות AI · הסיכומים נוצרים בשעות הלילה ומוצגים בבוקר</p>

      {data?.status === 'done' && (
        <div className="ai-meta-row">
          <span>{data.stats?.sessions ?? 0} שיחות בתקופה</span>
          <span>
            הופק: {data.generated_at ? new Date(data.generated_at).toLocaleString('he-IL') : '—'}
            <button type="button" className="ai-linkbtn" onClick={generate} disabled={generating}>
              {generating ? 'מפיק…' : 'הפק מחדש'}
            </button>
          </span>
        </div>
      )}

      {loading ? (
        <div className="card ai-card"><SkeletonRows rows={6} /></div>
      ) : error ? (
        <div className="card ai-card ai-status ai-status--danger">
          <div className="ai-status-icon"><Icon name="danger" size={22} /></div>
          <p>שגיאה בטעינת הדוח.</p>
          <button type="button" className="btn" onClick={() => load()}><Icon name="history" size={14} /> נסה שוב</button>
        </div>
      ) : !data || data.status !== 'done' ? (
        <ReportStatusNote status={data?.status ?? 'missing'} onGenerate={generate} generating={generating} />
      ) : tab === 'business' ? (
        <BusinessReportView report={(data.report ?? {}) as AiBusinessReport} />
      ) : (
        <div className="ai-agents-layout">
          {showSidebar && (
            <AgentsSidebar
              agents={agents}
              activeId={activeAgent}
              onPick={(id) => {
                setActiveAgent(id);
                document.getElementById(`agent-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            {agents.length === 0 ? (
              <div className="card ai-card ai-status">
                <div className="ai-status-icon"><Icon name="ai" size={22} /></div>
                <p>אין נציגים עם שיחות בתקופה זו</p>
              </div>
            ) : tab === 'agents' ? (
              <AgentsReportView agents={agents} teamAvgScore={data.stats?.team_avg_score} />
            ) : (
              <CampaignsReportView agents={agents} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
